import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Save, Loader2, Webhook, Send, Settings, Sparkles, Plus, Trash2, ChevronDown, ChevronUp, Pencil } from "lucide-react";

const API = "/api";

const WebhookPanel = ({ onClose }) => {
  const [webhooks, setWebhooks] = useState([]);
  const [landings, setLandings] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(null); // webhook id being edited
  const [form, setForm] = useState({ name: "", url: "", active: true, sources: [] });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchData = async () => {
    try {
      const [whRes, ldRes] = await Promise.all([
        axios.get(`${API}/admin/webhooks`),
        axios.get(`${API}/landings`),
      ]);
      setWebhooks(whRes.data || []);
      setLandings(ldRes.data || []);
    } catch {}
    setLoaded(true);
  };

  useEffect(() => { fetchData(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm({ name: "", url: "", active: true, sources: [] });
  };

  const startEdit = (wh) => {
    setEditing(wh.id);
    setForm({ name: wh.name || "", url: wh.url || "", active: wh.active, sources: wh.sources || [] });
  };

  const cancelEdit = () => { setEditing(null); };

  const toggleSource = (src) => {
    setForm(prev => ({
      ...prev,
      sources: prev.sources.includes(src) ? prev.sources.filter(s => s !== src) : [...prev.sources, src],
    }));
  };

  const handleSave = async () => {
    if (!form.url.trim()) { toast.error("URL obbligatorio"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/webhooks`, form);
        toast.success("Webhook creato");
      } else {
        await axios.put(`${API}/admin/webhooks/${editing}`, form);
        toast.success("Webhook aggiornato");
      }
      setEditing(null);
      await fetchData();
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo webhook?")) return;
    try {
      await axios.delete(`${API}/admin/webhooks/${id}`);
      toast.success("Webhook eliminato");
      await fetchData();
    } catch { toast.error("Errore eliminazione"); }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const res = await axios.post(`${API}/admin/webhooks/${id}/test`);
      if (res.data.success) {
        toast.success(`Test riuscito! Status: ${res.data.status_code}`);
      } else {
        toast.error(`Test fallito: ${res.data.message}`);
      }
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel test");
    } finally { setTesting(null); }
  };

  if (!loaded) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-testid="webhook-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl animate-fade-in-up overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
              <Webhook className="w-5 h-5 text-purple-600" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="webhook-title">
                Webhook ({webhooks.length})
              </h3>
              <p className="text-xs text-slate-400">Smista i lead a piu destinazioni contemporaneamente</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100" data-testid="webhook-close">x</button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
          {/* Webhook List */}
          {webhooks.map((wh) => (
            <div key={wh.id} className={`border rounded-xl transition-colors ${wh.active ? "border-purple-200 bg-purple-50/30" : "border-slate-100"}`} data-testid={`webhook-item-${wh.id}`}>
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(expanded === wh.id ? null : wh.id)}>
                <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${wh.active ? "bg-emerald-500" : "bg-slate-300"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{wh.name || wh.url}</p>
                  <p className="text-[10px] text-slate-400 font-mono truncate">{wh.url}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {wh.sources?.length > 0 && (
                    <span className="text-[9px] bg-purple-100 text-purple-600 font-bold px-1.5 py-0.5 rounded-full">{wh.sources.length} sorgenti</span>
                  )}
                  {wh.last_status && (
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${wh.last_status.includes("error") ? "bg-red-50 text-red-500" : "bg-emerald-50 text-emerald-600"}`}>
                      {wh.last_status}
                    </span>
                  )}
                  {expanded === wh.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>

              {expanded === wh.id && (
                <div className="px-4 pb-4 pt-1 border-t border-slate-100/50 space-y-2">
                  {wh.sources?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {wh.sources.map((s) => (
                        <span key={s} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{s}</span>
                      ))}
                    </div>
                  )}
                  {wh.last_triggered && (
                    <p className="text-[10px] text-slate-400">Ultimo invio: {new Date(wh.last_triggered).toLocaleString("it-IT")}</p>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <button onClick={(e) => { e.stopPropagation(); handleTest(wh.id); }} disabled={testing === wh.id}
                      className="flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-700 disabled:opacity-40" data-testid={`webhook-test-${wh.id}`}>
                      {testing === wh.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Test
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); startEdit(wh); }}
                      className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700" data-testid={`webhook-edit-${wh.id}`}>
                      <Pencil className="w-3.5 h-3.5" /> Modifica
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(wh.id); }}
                      className="flex items-center gap-1 text-xs font-medium text-red-400 hover:text-red-600" data-testid={`webhook-delete-${wh.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Elimina
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {webhooks.length === 0 && !editing && (
            <div className="text-center py-8 border border-dashed border-slate-200 rounded-xl">
              <Webhook className="w-6 h-6 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400 mb-1">Nessun webhook configurato</p>
              <button onClick={startNew} className="text-sm font-semibold text-purple-600 hover:text-purple-700">+ Aggiungi il primo</button>
            </div>
          )}

          {/* Edit / Create Form */}
          {editing && (
            <div className="border-2 border-purple-300 rounded-xl p-5 bg-purple-50/40 space-y-4" data-testid="webhook-form">
              <h4 className="text-sm font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                {editing === "new" ? "Nuovo Webhook" : "Modifica Webhook"}
              </h4>

              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Nome (opzionale)</label>
                <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="es: Zapier Cliente Milano" className="h-10 rounded-xl text-sm" data-testid="webhook-form-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Webhook URL *</label>
                <Input type="url" value={form.url} onChange={(e) => setForm(p => ({ ...p, url: e.target.value }))}
                  placeholder="https://hooks.zapier.com/hooks/catch/..." className="h-10 rounded-xl font-mono text-sm" data-testid="webhook-form-url" />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Sorgenti Lead</label>
                <p className="text-[10px] text-slate-400 mb-2">Se nessuna selezionata, riceve tutti i lead.</p>
                <div className="space-y-1.5" data-testid="webhook-form-sources">
                  <label className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer text-sm ${form.sources.includes("wizard") ? "border-purple-300 bg-purple-50" : "border-slate-100"}`}>
                    <input type="checkbox" checked={form.sources.includes("wizard")} onChange={() => toggleSource("wizard")} className="w-3.5 h-3.5 rounded text-purple-600" />
                    <Settings className="w-3.5 h-3.5 text-slate-400" /> <span className="font-medium text-slate-700 text-xs">Configuratore / Home</span>
                  </label>
                  <label className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer text-sm ${form.sources.includes("landing_all") ? "border-purple-300 bg-purple-50" : "border-slate-100"}`}>
                    <input type="checkbox" checked={form.sources.includes("landing_all")} onChange={() => toggleSource("landing_all")} className="w-3.5 h-3.5 rounded text-purple-600" />
                    <Sparkles className="w-3.5 h-3.5 text-slate-400" /> <span className="font-medium text-slate-700 text-xs">Tutte le Landing Promo</span>
                  </label>
                  {landings.length > 0 && !form.sources.includes("landing_all") && (
                    <div className="pl-4 space-y-1 border-l-2 border-slate-100 ml-2">
                      <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide mb-1">O seleziona landing specifiche</p>
                      {landings.map((ld) => {
                        const key = `landing:${ld.slug}`;
                        return (
                          <label key={ld.slug} className={`flex items-center gap-2.5 p-2 rounded-lg border cursor-pointer ${form.sources.includes(key) ? "border-purple-200 bg-purple-50/60" : "border-slate-50"}`}>
                            <input type="checkbox" checked={form.sources.includes(key)} onChange={() => toggleSource(key)} className="w-3 h-3 rounded text-purple-600" />
                            <span className="text-xs font-medium text-slate-700 truncate flex-1">{ld.title}</span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${ld.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{ld.status === "active" ? "attiva" : "pausa"}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold text-slate-700">Webhook attivo</span>
                  <p className="text-[10px] text-slate-400">I lead verranno inviati automaticamente</p>
                </div>
                <button onClick={() => setForm(p => ({ ...p, active: !p.active }))}
                  className={`relative w-11 h-6 rounded-full transition-colors ${form.active ? "bg-emerald-500" : "bg-slate-300"}`}
                  data-testid="webhook-form-toggle">
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform`}
                    style={{ transform: form.active ? "translateX(20px)" : "translateX(2px)" }} />
                </button>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <button onClick={cancelEdit} className="text-sm font-medium text-slate-500 hover:text-slate-700 px-4 py-2">Annulla</button>
                <button onClick={handleSave} disabled={saving}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-50"
                  data-testid="webhook-form-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editing === "new" ? "Crea Webhook" : "Salva"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between shrink-0">
          <details className="group">
            <summary className="text-xs font-semibold text-slate-400 cursor-pointer hover:text-slate-600 select-none">Formato dati inviati</summary>
            <pre className="mt-2 bg-slate-900 text-slate-300 rounded-xl p-3 text-[10px] overflow-x-auto leading-relaxed max-w-md">
{`GET {url}?event=new_lead&source=wizard
  &name=Mario&surname=Rossi
  &email=...&phone=...&ip=...
  &landing_slug=&product_name=...`}
            </pre>
          </details>
          {!editing && (
            <button onClick={startNew}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-full px-5 py-2.5 text-sm flex items-center gap-2"
              data-testid="webhook-add-button">
              <Plus className="w-4 h-4" /> Aggiungi Webhook
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebhookPanel;
